import type { ContextCacheUsage, ContextUsage } from '@reflex/shared';
import { cn } from '../../lib/utils';
import { formatTokenCount } from '../../lib/format';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export interface ContextUsageMeterProps {
  /** The context-usage snapshot to visualize. */
  usage: ContextUsage;
  className?: string;
}

const RING_SIZE = 16;
const STROKE = 2;
const RADIUS = (RING_SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const CENTER = RING_SIZE / 2;

// Thresholds at which the ring shifts tone to warn the user the window is
// filling up. Below the warning mark it stays muted so it reads as ambient.
const WARN_AT = 0.75;
const DANGER_AT = 0.9;

/** A fraction pinned to `[0, 1]`, with unusable input reading as empty. */
function clampFraction(fraction: number): number {
  if (!Number.isFinite(fraction)) return 0;
  return Math.min(1, Math.max(0, fraction));
}

/** A whole-percent reading of a `[0, 1]` fraction, guarding unusable input. */
function toPercent(fraction: number): number {
  return Math.round(clampFraction(fraction) * 100);
}

function StatRow({ label, value, nested }: { label: string; value: string; nested?: boolean }) {
  return (
    <>
      <dt className={cn('text-muted-foreground', nested && 'pl-3')}>{label}</dt>
      <dd className="text-right tabular-nums">{value}</dd>
    </>
  );
}

/**
 * The prompt-cache half of the tooltip: how much of the last request's prompt
 * was replayed from cache rather than re-sent, and — where the provider
 * reports it — how long the tokens this request wrote will live in the cache.
 *
 * The first three rows partition the prompt and sum to the `usedTokens` on the
 * line above. The expiry rows are a sub-breakdown of the written tokens, not a
 * fourth share of the prompt, so they are indented under that row — a flat
 * list would read as additive and overshoot the total.
 */
function CacheStats({ cache }: { cache: ContextCacheUsage }) {
  const cachedPercent = toPercent(cache.fraction);
  const ttl = cache.ttlTokens;

  return (
    <div
      className="border-border/60 mt-1.5 border-t pt-1.5"
      data-testid="context-usage-cache-stats"
      data-cached-percent={cachedPercent}
    >
      <p className="mb-1 font-medium">Prompt cache · {cachedPercent}% cached</p>
      <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <StatRow label="Read from cache" value={formatTokenCount(cache.readTokens)} />
        <StatRow label="Written to cache" value={formatTokenCount(cache.writeTokens)} />
        {ttl?.ephemeral5m ? (
          <StatRow nested label="Expires in 5 min" value={formatTokenCount(ttl.ephemeral5m)} />
        ) : null}
        {ttl?.ephemeral1h ? (
          <StatRow nested label="Expires in 1 hour" value={formatTokenCount(ttl.ephemeral1h)} />
        ) : null}
        <StatRow label="Sent uncached" value={formatTokenCount(cache.freshTokens)} />
      </dl>
    </div>
  );
}

/**
 * A small circular gauge showing how full a model's context window is, sized to
 * sit alongside the composer's icon buttons. The ring fills clockwise from the
 * top and shifts from muted to warning to danger as the window approaches full.
 * Hover reveals the exact token figures, plus the prompt-cache breakdown of the
 * most recent request when the provider reports one.
 */
export function ContextUsageMeter({ usage, className }: ContextUsageMeterProps) {
  const fraction = clampFraction(usage.fraction);
  const percent = Math.round(fraction * 100);
  const cache = usage.cache;
  const tone =
    fraction >= DANGER_AT
      ? 'text-status-error'
      : fraction >= WARN_AT
        ? 'text-status-warning'
        : 'text-muted-foreground';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          // Focusable so the figures behind the tooltip have a keyboard path;
          // Radix opens on focus as well as hover.
          tabIndex={0}
          aria-label={
            cache
              ? `Context ${percent}% full, ${toPercent(cache.fraction)}% of the last prompt read from cache`
              : `Context ${percent}% full`
          }
          data-testid="context-usage-meter"
          data-fraction={fraction.toFixed(2)}
          className={cn('inline-flex h-7 w-7 items-center justify-center', tone, className)}
        >
          <svg
            width={RING_SIZE}
            height={RING_SIZE}
            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              strokeWidth={STROKE}
              className="stroke-current opacity-25"
            />
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={`${CIRCUMFERENCE * fraction} ${CIRCUMFERENCE}`}
              transform={`rotate(-90 ${CENTER} ${CENTER})`}
              className="stroke-current transition-[stroke-dasharray] duration-500"
            />
          </svg>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 text-left">
        <p>
          {percent}% of context used ({formatTokenCount(usage.usedTokens)} /{' '}
          {formatTokenCount(usage.maxTokens)} tokens)
        </p>
        {cache && <CacheStats cache={cache} />}
      </TooltipContent>
    </Tooltip>
  );
}
